import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import type { ThreeEvent } from '@react-three/fiber'
import {
  BOARD_POSITIONS,
  BOARD_RADIUS,
  BOARD_THICKNESS,
  BOARD_TOP_RADIUS,
  BOARD_TOP_Y,
  WELL_RADIUS,
  boardToXZ,
  getBoardIrregularity,
} from './constants'
import { createBoardTextures, createHoleTexture } from './textures'
import type { BoardTextures } from './textures'

interface BoardMeshProps {
  onPositionClick?: (q: number, r: number) => void
  // When provided (e.g. shared across several boards), the caller owns disposal.
  textures?: BoardTextures
  // Distant/decorative boards: drop the bump + roughness maps, whose normals alias
  // into radial moiré at grazing angles. The colour map alone reads fine at range.
  simplified?: boolean
}

const PICK_RADIUS = WELL_RADIUS * 2

export default function BoardMesh({ onPositionClick, textures: providedTextures, simplified = false }: BoardMeshProps) {
  const textures = useMemo(() => providedTextures ?? createBoardTextures(), [providedTextures])

  useEffect(
    () => () => {
      if (!providedTextures) Object.values(textures).forEach((texture) => texture.dispose())
    },
    [textures, providedTextures],
  )

  // Geometry holes for simplified (landing) boards: one instanced sprite per
  // position instead of a baked hole grid, so nothing moirés at grazing angles.
  const holeTexture = useMemo(() => (simplified ? createHoleTexture() : null), [simplified])
  useEffect(() => () => holeTexture?.dispose(), [holeTexture])
  const holesRef = useRef<THREE.InstancedMesh>(null)

  useLayoutEffect(() => {
    const mesh = holesRef.current
    if (!mesh) return
    const dummy = new THREE.Object3D()
    BOARD_POSITIONS.forEach((pos, i) => {
      const { x, z } = boardToXZ(pos)
      const irr = getBoardIrregularity(pos)
      const r = irr.wellRadius * 1.55
      dummy.position.set(x, BOARD_TOP_Y + 0.004, z)
      dummy.rotation.set(-Math.PI / 2, 0, 0)
      dummy.scale.set(r * irr.ovalX, r * irr.ovalZ, 1)
      dummy.updateMatrix()
      mesh.setMatrixAt(i, dummy.matrix)
    })
    mesh.instanceMatrix.needsUpdate = true
  }, [simplified])

  const handleBoardClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (!onPositionClick) return
      let nearest = BOARD_POSITIONS[0]
      let nearestDistance = Infinity

      for (const pos of BOARD_POSITIONS) {
        const { x, z } = boardToXZ(pos)
        const distance = Math.hypot(event.point.x - x, event.point.z - z)
        if (distance < nearestDistance) {
          nearest = pos
          nearestDistance = distance
        }
      }

      if (nearestDistance <= PICK_RADIUS) {
        event.stopPropagation()
        onPositionClick(nearest.q, nearest.r)
      }
    },
    [onPositionClick],
  )

  return (
    <group onClick={onPositionClick ? handleBoardClick : undefined}>
      {/* Solid wooden body */}
      <mesh position={[0, BOARD_TOP_Y - BOARD_THICKNESS / 2, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[BOARD_RADIUS, BOARD_RADIUS * 0.97, BOARD_THICKNESS, 180]} />
        <meshStandardMaterial color="#5c3a20" roughness={0.85} metalness={0} envMapIntensity={0.25} />
      </mesh>

      {/* Carved playing surface */}
      <mesh position={[0, BOARD_TOP_Y, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <circleGeometry args={[BOARD_TOP_RADIUS, 220]} />
        <meshStandardMaterial
          map={textures.color}
          roughnessMap={simplified ? undefined : textures.roughness}
          bumpMap={simplified ? undefined : textures.bump}
          bumpScale={simplified ? 0 : 0.05}
          roughness={simplified ? 0.9 : 1}
          metalness={0}
          envMapIntensity={simplified ? 0.28 : 0.4}
        />
      </mesh>

      {/* Crisp geometry holes (landing boards) — never a baked grid, so no moiré */}
      {simplified && holeTexture && (
        <instancedMesh
          ref={holesRef}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          args={[undefined as any, undefined as any, BOARD_POSITIONS.length]}
          frustumCulled={false}
        >
          <circleGeometry args={[1, 28]} />
          <meshBasicMaterial map={holeTexture} transparent depthWrite={false} />
        </instancedMesh>
      )}

      {/* Rounded lip that catches the light around the rim */}
      <mesh position={[0, BOARD_TOP_Y - 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[BOARD_TOP_RADIUS, 0.09, 20, 200]} />
        <meshStandardMaterial color="#6b4325" roughness={0.55} metalness={0.1} envMapIntensity={0.55} />
      </mesh>
    </group>
  )
}
